import axios from "axios";

/**
 * Shared helpers to extract information from API errors.
 *
 * Our axios instances reject with the raw axios error (see interceptors/Interceptor.ts),
 * so the backend payload lives under `error.response.data`. These helpers narrow the
 * error with `axios.isAxiosError` and fall back gracefully for non-axios errors.
 *
 * They centralize the EXTRACTION of fields (message, status, traceId); composing the
 * final user-facing message remains the caller's responsibility.
 */

/** Shape of the error payload the backend returns under `response.data`. */
interface ApiErrorData {
  message?: string;
  error?: string;
  traceId?: string;
}

/**
 * Reads the most relevant error message from an API error.
 * Order: `response.data.message` -> `response.data.error` -> `error.message` -> `fallback`.
 */
export const getApiErrorMessage = (error: unknown, fallback?: string): string => {
  if (axios.isAxiosError<ApiErrorData>(error)) {
    const data = error.response?.data;
    return data?.message || data?.error || error.message || fallback || "";
  }
  if (error instanceof Error) {
    return error.message || fallback || "";
  }
  return fallback || "";
};

/**
 * Reads the HTTP status from an API error.
 * Falls back to a `status` property on non-axios errors when present.
 */
export const getApiErrorStatus = (error: unknown): number | undefined => {
  if (axios.isAxiosError(error)) {
    return error.response?.status;
  }
  if (typeof error === "object" && error !== null && "status" in error) {
    const status = (error as { status?: unknown }).status;
    return typeof status === "number" ? status : undefined;
  }
  return undefined;
};

/** Reads the backend trace identifier from `response.data.traceId`, when present. */
export const getApiErrorTraceId = (error: unknown): string | undefined => {
  if (axios.isAxiosError<ApiErrorData>(error)) {
    return error.response?.data?.traceId;
  }
  return undefined;
};
